import React, {RefObject} from "react";
import Label from "../../../../../../components/Label/Label";
import {arr} from "../../../../../../lib/helpers/renderhelper";
import {ResultError, Return} from "../../../../../../lib/result";
import {SectionProps} from "../../../../../../components/Drawer/ItemDrawer";
import {ClusterV1HelmRelease} from "../../../../../../../gen/models/clusterV1HelmRelease";
import Input from "../../../../../../components/Input/Input";
import Section from "../../../../../../components/Drawer/Section/Section";
import styles from "./HelmChartConfig.module.scss"
import constants from "../../../../../../constants/constants";
import YAMLEditor from "../../../../../../components/YAMLEditor/YAMLEditor";
import AutoComplete from "../../../../../../components/AutoComplete/AutoComplete";
import {StorageV1HelmChart} from "../../../../../../../gen/models/storageV1HelmChart";
import {HelmRelease} from "../../AppsTable/AppsTable";
import {ManagementV1App} from "../../../../../../../gen/models/managementV1App";
import {randomString} from "../../../../../../lib/helper";
import AppParameters from "../../../../../../components/Drawer/AppParameters/AppParameters";
import {StorageV1AppParameter} from "../../../../../../../gen/models/storageV1AppParameter";
import SectionExpander from "../../../../../../components/Drawer/SectionExpander/SectionExpander";

interface HelmChartConfigState {
    releaseName: string | undefined;
    namespace: string | undefined;

    chartVersion: string | undefined;
    chartValues: string | undefined;
    
    parameters: StorageV1AppParameter[] | undefined;
}

interface HelmChartConfigProps extends SectionProps {
    show: boolean;
    release?: HelmRelease;
    namespace?: string;

    initialVersion?: string;
    initialValues?: string;

    selectedChart: StorageV1HelmChart | undefined;
    selectedApp: ManagementV1App | undefined;
    clusterApp: boolean;
}

export function setReleaseMetadata(obj: ClusterV1HelmRelease, namespace: string | undefined, releaseName: string | undefined, selectedApp: ManagementV1App | undefined, isClusterApp: boolean) {
    if (!obj.metadata) {
        obj.metadata = {};
    }

    obj.metadata.namespace = namespace || "loft-apps";
    obj.metadata.name = releaseName || obj.spec?.chart?.name || "release-" + randomString(5);
    if (!obj.metadata.labels) {
        obj.metadata.labels = {}
    } 
    if (!obj.metadata.annotations) {
        obj.metadata.annotations = {}
    }
    if (!obj.spec) {
        obj.spec = {};
    }
    if (!obj.spec.annotations) {
        obj.spec.annotations = {};
    }

    obj.metadata.labels[constants.LoftHelmReleaseAppLabel] = "true";
    if (selectedApp?.metadata?.name) {
        obj.spec.annotations[constants.LoftHelmReleaseAppGenerationAnnotation] = selectedApp.metadata.generation + "";
        obj.spec.annotations[constants.LoftHelmReleaseAppNameAnnotation] = selectedApp.metadata.name;
    }
    if (isClusterApp) {
        obj.metadata.labels[constants.LoftHelmReleaseClusterAppLabel] = "true";
    }
}

export default class HelmChartConfig extends React.PureComponent<HelmChartConfigProps, HelmChartConfigState> {
    state: HelmChartConfigState = {
        releaseName: this.props.release?.release.metadata?.name || this.props.selectedApp?.metadata?.name,
        namespace: this.props.namespace || this.props.release?.release.metadata?.namespace || "loft-apps",
        chartVersion: this.props.release?.release.spec?.chart?.version || this.props.initialVersion || this.props.selectedApp?.spec?.config?.chart?.version,
        chartValues: this.props.release?.release.spec?.values || this.props.initialValues || this.props.selectedApp?.spec?.config?.values,
        
        parameters: this.props.release?.app?.spec?.parameters || this.props.selectedApp?.spec?.parameters,
    };

    create = (obj: ClusterV1HelmRelease): ResultError => {
        if (!this.props.show) {
            return Return.Ok();
        }

        setReleaseMetadata(obj, this.state.namespace, this.state.releaseName, this.props.selectedApp, this.props.clusterApp);
        return this.update(obj);
    };

    update = (obj: ClusterV1HelmRelease): ResultError => {
        if (!this.props.show) {
            return Return.Ok();
        }
        obj.spec!.chart!.version = this.state.chartVersion;
        obj.spec!.values = this.state.chartValues;
        return Return.Ok();
    };

    selectCustomHelm = () => {
        this.setState({
            releaseName: undefined,
            chartVersion: undefined,
            chartValues: undefined,
            parameters: undefined,
        });
    }

    selectApp = (app: ManagementV1App | undefined) => {
        if (!app) {
            this.setState({
                parameters: undefined,
            });
            
            return;
        }

        this.setState({
            releaseName: app.metadata?.name,
            chartVersion: app.spec?.config?.chart?.version,
            chartValues: app.spec?.config?.values,
            parameters: app.spec?.parameters,
        });
    }
    
    selectValues = (values: string | undefined) => {
        this.setState({
            chartValues: values,
        })
    }

    selectChart = (chart: StorageV1HelmChart | undefined) => {
        if (!chart) {
            this.setState({
                parameters: undefined,
            });
            
            return;
        }

        this.setState({
            chartVersion: arr(chart.versions).length > 0 ? chart.versions![0] : undefined,
            chartValues: undefined,
            parameters: undefined,
        });
    };
    
    renderValues = () => {
        const valuesInput = <React.Fragment>
            <Label className={styles["chart-values"]}>
                <span>Chart Values</span>
            </Label>
            <YAMLEditor value={this.state.chartValues}
                        minLines={5}
                        maxLines={100}
                        onChange={e => this.setState({chartValues: e})}
                        placeholder={`ingress:
  enabled: false
...`}  />
        </React.Fragment>
        if (this.props.selectedApp) {
            return <SectionExpander name={"Helm Values"}>
                {valuesInput}
            </SectionExpander>
        }
        
        return valuesInput
    } 

    render() {
        if (this.props.mode === "batch" || !this.props.show) {
            return null;
        }

        return <Section title={"Release Configuration"}>
            <Label>Release Name</Label>
            <Input readOnly={this.props.mode === "update"} value={this.state.releaseName} onChange={e => this.setState({releaseName: e.target.value})} placeholder={"Leave empty for auto-generated release name"} />
            <div className={"row"}>
                <div style={{flexGrow: 3}}>
                    <Label>Kubernetes Namespace</Label>
                    <Input readOnly={this.props.mode === "update" || !!this.props.namespace} value={this.state.namespace} onChange={e => this.setState({namespace: e.target.value})} placeholder={"loft-apps"} />
                </div>
                <div style={{flexGrow: 2}}>
                    <Label>Chart Version</Label>
                    {
                        this.props.selectedChart ? <AutoComplete value={this.state.chartVersion}
                                                                 style={{ width: "100%" }}
                                                                 showArrow={true}
                                                                 allowClear={true}
                                                                 placeholder="Select Version"
                                                                 onChange={value => this.setState({chartVersion: value})}
                                                                 options={this.props.selectedChart?.versions?.map(version => ({label: version, value: version}))}
                                                                 filterOption={() => true} /> : <Input value={this.state.chartVersion} onChange={e => this.setState({chartVersion: e.target.value})} placeholder={"latest"} />
                    }
                </div>
            </div>
            {this.renderValues()}
        </Section>
    }
}